import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Item {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image_url: string | null;
  published_at: string | null;
  body_html: string | null;
}

const formatDate = (iso: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

const readingTime = (text: string | null) => {
  if (!text) return null;
  const words = text.replace(/<[^>]+>/g, " ").trim().split(/\s+/).length;
  const min = Math.max(1, Math.round(words / 200));
  return `${min} min`;
};

const BlogArticlesGrid = () => {
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    (async () => {
      const { data: arts } = await (supabase as any)
        .from("blog_articles")
        .select("id,slug,title,excerpt,published_at,body_html")
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(60);
      const list = arts || [];
      const ids = list.map((a: any) => a.id);
      let imgs: any[] = [];
      if (ids.length) {
        const { data } = await (supabase as any)
          .from("blog_article_images")
          .select("article_id,image_url,slot")
          .in("article_id", ids)
          .order("slot");
        imgs = data || [];
      }
      setItems(
        list.map((a: any) => ({
          ...a,
          image_url:
            imgs.find((i) => i.article_id === a.id)?.image_url || null,
        }))
      );
    })();
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="mt-16" aria-labelledby="blog-grid-heading">
      <h2
        id="blog-grid-heading"
        className="font-heading text-2xl md:text-3xl font-bold text-primary mb-8"
      >
        Blog & Conseils Voyage
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {items.map((it) => {
          const date = formatDate(it.published_at);
          const rt = readingTime(it.body_html);
          return (
            <article
              key={it.id}
              itemScope
              itemType="https://schema.org/BlogPosting"
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              <Link
                to={`/guide/blog/${it.slug}`}
                aria-label={it.title}
                className="block relative overflow-hidden aspect-video bg-muted"
              >
                {it.image_url ? (
                  <img
                    src={it.image_url}
                    alt={it.title}
                    itemProp="image"
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={338}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10" />
                )}
              </Link>
              <div className="flex flex-1 flex-col p-5 space-y-3">
                <h3
                  itemProp="headline"
                  className="font-heading text-lg font-bold text-foreground leading-snug line-clamp-2"
                >
                  <Link
                    to={`/guide/blog/${it.slug}`}
                    className="hover:text-primary transition-colors"
                  >
                    {it.title}
                  </Link>
                </h3>
                {it.excerpt && (
                  <p
                    itemProp="description"
                    className="text-sm text-muted-foreground leading-relaxed line-clamp-3"
                  >
                    {it.excerpt}
                  </p>
                )}
                <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-muted-foreground border-t border-border/60">
                  {date && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      <time
                        itemProp="datePublished"
                        dateTime={it.published_at ?? undefined}
                      >
                        {date}
                      </time>
                    </span>
                  )}
                  {rt && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {rt} de lecture
                    </span>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default BlogArticlesGrid;